export interface TimelineEntry {
    id: number;
    url: string;
}

export interface TimelineState {
    entries: TimelineEntry[];
    // Index of the current page. It equals `entries.length` when the user moved to the
    // placeholder of the next page before it was loaded.
    index: number;
    nextId: number;
    loadNext: boolean;
    moveOnNextLoaded: boolean;
}

export type IsSamePage = (currentUrl: string, newUrl: string) => boolean;

// History state of a navigation that stays on the current page of the timeline (the audio player
// moving to another episode): the page follows the new url instead of a new page being added.
export const samePageHistoryState = { timelineSamePage: true };

export function isSamePageHistoryState(state: unknown): boolean {
    return (state as { timelineSamePage?: boolean } | null)?.timelineSamePage === true;
}

export type TimelineAction =
    | {
          type: 'locationChanged';
          url: string;
          nextUrl: string | null;
          isSamePage: IsSamePage;
          samePage?: boolean;
      }
    | { type: 'goTo'; index: number }
    | { type: 'pageNavigated'; id: number; url: string }
    | { type: 'loadNext' }
    | { type: 'navigateNext' }
    | { type: 'nextLoaded'; url: string };

export function getPathname(url: string | null): string | null {
    return url !== null ? url.split('?')[0] : null;
}

export function createTimelineState(url: string): TimelineState {
    return {
        entries: [{ id: 0, url }],
        index: 0,
        nextId: 1,
        loadNext: false,
        moveOnNextLoaded: false,
    };
}

export function getCurrentEntry(state: TimelineState): TimelineEntry | null {
    return state.entries[state.index] || null;
}

// The placeholder of the next page is shown while the current page is the last loaded one.
export function hasNextPlaceholder(state: TimelineState, withoutNext = false): boolean {
    return !withoutNext && state.index >= state.entries.length - 1;
}

function appendEntry(state: TimelineState, url: string, afterIndex: number): TimelineState {
    const entries = [...state.entries.slice(0, afterIndex + 1), { id: state.nextId, url }];
    return {
        ...state,
        entries,
        index: entries.length - 1,
        nextId: state.nextId + 1,
        loadNext: false,
        moveOnNextLoaded: false,
    };
}

export function timelineReducer(state: TimelineState, action: TimelineAction): TimelineState {
    const { entries, index } = state;
    const current = getCurrentEntry(state);

    switch (action.type) {
        // The browser location changed: back/forward, a link outside the pages, or a
        // navigation from the current page.
        case 'locationChanged': {
            const { url, nextUrl, isSamePage, samePage = false } = action;
            if (current !== null && current.url === url) {
                return state;
            }
            const replaceCurrent = {
                ...state,
                entries: entries.map((entry) =>
                    current !== null && entry.id === current.id ? { ...entry, url } : entry,
                ),
            };
            if (current !== null && (isSamePage(current.url, url) || current.url === nextUrl)) {
                return replaceCurrent;
            }
            if (index > 0 && entries[index - 1].url === url) {
                return { ...state, index: index - 1 };
            }
            if (index < entries.length - 1 && entries[index + 1].url === url) {
                return { ...state, index: index + 1, loadNext: false };
            }
            // After the neighbours: back or forward to a page keeps moving the timeline
            if (current !== null && samePage) {
                return replaceCurrent;
            }
            return appendEntry(state, url, Math.min(index, entries.length - 1));
        }

        // A drag or a click on a neighbour moved the timeline.
        case 'goTo': {
            const newIndex = Math.max(0, Math.min(action.index, entries.length));
            if (newIndex === index) {
                return state;
            }
            let { loadNext } = state;
            if (newIndex === entries.length) {
                // On the placeholder: load the next page right away.
                loadNext = true;
            } else if (newIndex === entries.length - 1) {
                // On the last page: wait before loading the next one.
                loadNext = false;
            }
            return { ...state, index: newIndex, loadNext, moveOnNextLoaded: false };
        }

        // A page that is not the current one changed its own location: only that entry
        // follows, the browser history is left untouched.
        case 'pageNavigated': {
            const { id, url } = action;
            return {
                ...state,
                entries: entries.map((entry) => (entry.id === id ? { ...entry, url } : entry)),
            };
        }

        case 'loadNext':
            return state.loadNext ? state : { ...state, loadNext: true };

        // The user asked for the next page before it was loaded.
        case 'navigateNext':
            return { ...state, loadNext: true, moveOnNextLoaded: true };

        case 'nextLoaded': {
            const { url } = action;
            const last = entries[entries.length - 1];
            if (last.url === url) {
                return state;
            }
            const onPlaceholder = index === entries.length;
            const newState = appendEntry(state, url, entries.length - 1);
            return onPlaceholder || state.moveOnNextLoaded ? newState : { ...newState, index };
        }

        default:
            return state;
    }
}
